import Highlight from '../components/Highlight';
import Heading from '../components/Heading';

const Actions = () => {
  return (
    <div>
      <p>
        Use actions for asynchronous data operations, for example REST API calls.
      </p>
      <p>The <code>context</code> argument is
        an object with two keys - <code>mutations</code> and <code>actions</code>. You can use it to dispatch another
        action that's dependent on your current action's data and to alter the current state.
      </p>
      <Highlight language="javascript" code={indexCode} />

      <hr />

      <Heading id="useAction">useAction</Heading>
      <h3 className="heading-types">
        useAction&lt;T&gt;(actionName: string) => (args?: any) => Promise&lt;T&gt;
      </h3>
      <p>
        To use an action in a component, use the <code>useAction()</code> hook. Actions always return a promise. They don't get re-created, so you're safe with
        using them as dependencies
        for <code>useEffect</code> or <code>useCallback</code>.
      </p>
      <Highlight language="javascript" code={componentCode} />

      <hr />

      <Heading>useActions</Heading>
      <h3 className="heading-types">
        useActions(values: string[]) => ((args?: any) => Promise&lt;any&gt;)[]
      </h3>
      <p>
        If you wish to use more actions in a component, you can use the <code>useActions()</code> hook instead of
        using useAction() multiple times. The returned functions will be in the same order as provided arguments.
      </p>
      <Highlight language="javascript">
        {componentCode4}
      </Highlight>

      <hr />

      <Heading id="useActionOnMount">useActionOnMount</Heading>
      <h3 className="heading-types">
        useActionOnMount&lt;T&gt;(actionName: string, ...params: any[]) => void;
      </h3>
      <p>
        The hook <code>useActionOnMount()</code> was created for user's convenience, where the
        action will
        execute on
        component mount only, unless the provided parameters change. Each parameter change will trigger a re-fetch. This
        saves the user the need to
        write <code>useEffect</code> code and serves as a wrapper for it.
      </p>
      <Highlight language="javascript" code={componentCode3} />

      <hr />

      <Heading>useAction with modules</Heading>
      <p>
        To access a module's action, use the module name as the prefix.
          <br />
          <code>const action = useAction('counter/COUNTER_FETCH')</code>
      </p>
      <Highlight language="javascript" code={modulesCode} />
    </div>
  )
}

const indexCode = `const store = {
  state: {
    user: null,
    posts: []
  },
  mutations: {
    USER_SET (state, data) {
      state.user = data
    }
    POSTS_SET (state, data) {
      state.posts = data
    }
  },
  actions: {
    async USER_POSTS_FETCH (context, userId) {
      const data = await userPostsCall(userId)
      context.mutations.POSTS_SET(data)
    }
    async USER_FETCH (context) {
      // fetch the current user
      const user = await userApiCall()
      context.mutations.USER_SET(user)
      
      // find out their ID and based on that dispatch an action to fetch their posts
      context.actions.USER_POSTS_FETCH(user.id)
    }
  }
}
`

const componentCode = `import { useEffect } from 'react'
import { useAction, useGetter } from 'vuex-but-for-react'

const UserComponent = () => {
  const onUserFetch = useAction('USER_FETCH');
  
  useEffect(() => {
    onUserFetch();
  }, [onUserFetch])
  
  const user = useGetter('user');
  
  return (
    ...
  )
}`

const componentCode3 = `import { useActionOnMount, useGetter } from 'vuex-but-for-react'

const UserComponent = () => {
  useActionOnMount('USER_FETCH');
  const user = useGetter('user');
  
  return (
    ...
  )
}`

const componentCode4 = `const [handleUserFetch, handlePostsFetch] = useActions(['USER_FETCH', 'USER_POSTS_FETCH']);`

const modulesCode = `const store = {
  state: {
    ...
  },
  mutations: {
    ...
  },
  modules: {
    counter: {
      state: { count: 0 },
      mutations: {
        COUNTER_SET (state, newValue) {
          state.count = newValue
        }
      },
      actions: {
        async COUNTER_FETCH (context) {
          const data = await dummyApiCall()
          // a module does not have access to the parent actions and mutations
          context.mutations.COUNTER_SET(data)
        }
      }
    }
  }
}
`

export default Actions;
